import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Vitamina brand colors
const BRAND_BLUE = [26, 86, 219];      // #1A56DB
const BRAND_BLUE_LIGHT = [59, 130, 246]; // #3B82F6
const SLATE_900 = [15, 23, 42];
const SLATE_600 = [71, 85, 105];
const SLATE_400 = [148, 163, 184];
const SLATE_50 = [248, 250, 252];

function fmtMoney(value) {
  if (value === null || value === undefined || isNaN(value)) return '$0';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value);
}

function fmtNumber(value) {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return new Intl.NumberFormat('es-MX').format(Math.round(value));
}

async function loadImageDataUrl(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Export the Presupuesto Mensual calculation summary to a branded PDF.
 * @param {object} opts
 * @param {string} opts.platform - "google" | "meta" | "tiktok"
 * @param {object} opts.calculations - MetaTraffic calculations object
 * @param {object} [opts.viabilidad] - viability object (optional)
 */
export async function exportPresupuestoPDF({ platform, calculations, viabilidad }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  // --- Header band ---
  doc.setFillColor(...BRAND_BLUE);
  doc.rect(0, 0, pageWidth, 90, 'F');
  doc.setFillColor(...BRAND_BLUE_LIGHT);
  doc.rect(0, 84, pageWidth, 6, 'F');

  // Logo
  const logoData = await loadImageDataUrl('/logo-vitamina-icon.png');
  if (logoData) {
    try { doc.addImage(logoData, 'PNG', margin, 22, 46, 46); } catch (_) { /* noop */ }
  }

  // Title & subtitle
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Presupuesto Publicitario Mensual', margin + 60, 44);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text('Vitamina Online · Calculadora de Pauta', margin + 60, 62);

  // Platform label (top right)
  const platformLabel = platform === 'google' ? 'Google Ads' : platform === 'meta' ? 'Meta Ads' : 'TikTok Ads';
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  const todayStr = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(`Plataforma: ${platformLabel}`, pageWidth - margin, 44, { align: 'right' });
  doc.text(todayStr, pageWidth - margin, 62, { align: 'right' });

  // --- Main headline ---
  let y = 130;
  doc.setTextColor(...SLATE_600);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Resultados estimados', margin, y);

  y += 22;
  doc.setFillColor(240, 247, 255);
  doc.setDrawColor(199, 220, 253);
  doc.roundedRect(margin, y - 16, pageWidth - margin * 2, 70, 10, 10, 'FD');
  doc.setTextColor(...BRAND_BLUE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PRESUPUESTO MENSUAL', margin + 16, y + 2);
  doc.setFontSize(26);
  doc.text(fmtMoney(calculations.presupuestoMensual), margin + 16, y + 32);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...SLATE_600);
  doc.text('Inversión total recomendada en publicidad digital', margin + 16, y + 48);

  y += 90;

  // --- Results table ---
  const rows = [
    ['Leads necesarios', fmtNumber(calculations.leadsNecesarios), 'Prospectos a generar para alcanzar tu meta'],
    ['Clics necesarios', fmtNumber(calculations.clicsNecesarios), 'Visitas requeridas según tu tasa de conversión'],
    ['CPL máximo', fmtMoney(calculations.cplMaximo), 'Costo Por Lead: límite por cada prospecto'],
    ['CPC máximo', fmtMoney(calculations.cpcMaximo), 'Costo Por Clic: techo por cada visita'],
    ['ROAS esperado', `${calculations.roasEsperado.toFixed(1)}x`, 'Retorno por cada $1 invertido en publicidad'],
    ['Ingresos proyectados', fmtMoney(calculations.ingresosProyectados), `Con ${calculations.ventas} venta${calculations.ventas === 1 ? '' : 's'} de ticket ${fmtMoney(calculations.ticket)}`],
    ['Intensidad del marketing', `${calculations.intensidadMkt.toFixed(1)}%`, 'Porcentaje del ingreso invertido en publicidad'],
    [`CPA ${calculations.cpAFueEstimado ? '(estimado 10% ticket)' : 'objetivo'}`, fmtMoney(calculations.cpaEfectivo), 'Costo máximo por adquirir un cliente'],
  ];

  autoTable(doc, {
    startY: y,
    head: [['Métrica', 'Valor', 'Descripción']],
    body: rows,
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 10,
      cellPadding: 8,
      lineColor: [226, 232, 240],
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: SLATE_50,
      textColor: SLATE_900,
      fontStyle: 'bold',
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 160, fontStyle: 'bold', textColor: SLATE_900 },
      1: { cellWidth: 110, halign: 'right', textColor: BRAND_BLUE, fontStyle: 'bold' },
      2: { textColor: SLATE_600, fontSize: 9 },
    },
    margin: { left: margin, right: margin },
  });

  y = doc.lastAutoTable.finalY + 18;

  // --- Viability card ---
  if (viabilidad) {
    const ok = viabilidad.esViable;
    const bg = ok ? [236, 253, 245] : [254, 242, 242];
    const border = ok ? [134, 239, 172] : [252, 165, 165];
    const fg = ok ? [5, 122, 85] : [185, 28, 28];

    const boxHeight = 58;
    if (y + boxHeight > pageHeight - 120) { doc.addPage(); y = margin; }
    doc.setFillColor(...bg);
    doc.setDrawColor(...border);
    doc.roundedRect(margin, y, pageWidth - margin * 2, boxHeight, 8, 8, 'FD');
    doc.setTextColor(...fg);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(ok ? 'Presupuesto viable' : 'Presupuesto insuficiente', margin + 14, y + 20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const viabText = ok
      ? `CPC máximo ${fmtMoney(viabilidad.cpcMaximo)} cubre el CPC ${viabilidad.fuenteCpc} (${fmtMoney(viabilidad.cpcReferencia)}). Tienes margen real para alcanzar el objetivo.`
      : `CPC máximo ${fmtMoney(viabilidad.cpcMaximo)} está ${fmtMoney(viabilidad.diferencia)} por debajo del CPC ${viabilidad.fuenteCpc} (${fmtMoney(viabilidad.cpcReferencia)}). Ajusta presupuesto o meta de ventas.`;
    const lines = doc.splitTextToSize(viabText, pageWidth - margin * 2 - 28);
    doc.text(lines, margin + 14, y + 36);
    y += boxHeight + 18;
  }

  // --- Disclaimer ---
  if (y + 90 > pageHeight - 60) { doc.addPage(); y = margin; }
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, pageWidth - margin * 2, 70, 6, 6, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...SLATE_900);
  doc.text('Aviso importante', margin + 12, y + 16);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE_600);
  doc.setFontSize(8);
  const disclaimer = 'Los valores mostrados son estimaciones basadas en benchmarks de la industria y las variables ingresadas. Los resultados reales dependen de la calidad creativa, la segmentación, el período de optimización del algoritmo y factores externos del mercado. No constituyen una garantía de rendimiento ni una oferta comercial formal.';
  const disclaimerLines = doc.splitTextToSize(disclaimer, pageWidth - margin * 2 - 24);
  doc.text(disclaimerLines, margin + 12, y + 30);

  // --- Footer ---
  const footerY = pageHeight - 30;
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...SLATE_400);
  doc.text('© Vitamina Online · calculatupauta.com', margin, footerY);
  doc.text(`Página 1 de ${doc.internal.getNumberOfPages()}`, pageWidth - margin, footerY, { align: 'right' });

  const filename = `presupuesto-vitamina-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}
